/**
 * ChartStore3 — per-panel slots (0–3) for desktop multi-chart; slot 0 is used
 * for mobile and chart popup. Promise-based data loading with batch processing.
 */

import { create } from "zustand";
import type { Symbol } from "../../symbols/types";
import { aggregationWorkerManager } from "../workers/WorkerManager";
import type {
  OHLCData,
  OHLCRequest,
  OHLCBatchResponse,
  TimeFrame,
} from "../types";
import enhancedOHLCIndexDB from "../indexdb";
import {
  generateAccountSymbolKeyForCurrent,
  getCurrentAccount,
} from "../utils/accountHelpers";
import { ohlcIdbDayCache } from "../workers/OhlcIdbCacheManager";
import {
  isOhlcPrefetchTarget,
  ohlcBarsFromBatch,
} from "@/lib/market-data-cache/ohlc-prefetch";
import { generateAccountSymbolKey } from "../indexdb";
import {
  CHART_PANEL_IDS,
  type ChartPanelId,
} from "./chartPanelTypes";
import { dedupeSortedAscending, mergeDedupeSorted } from "./ohlcMerge";
import { scheduleDeskWrite } from "@/lib/chart-desk/write";

/** In-flight GetOHLCData dedupe (same symbol + range). */
const inflightOhLcRequestKeys = new Set<string>();

function ohlcRequestKey(symbol: string, from: Date, to: Date): string {
  return `${symbol}:${from.getTime()}:${to.getTime()}`;
}

/** O(n) verify; only sorts + dedupes when the series is actually out of order. */
function ensureAscendingDeduped(data: OHLCData[]): OHLCData[] {
  for (let i = 1; i < data.length; i++) {
    if (data[i].timestamp <= data[i - 1].timestamp) {
      return dedupeSortedAscending(
        [...data].sort((a, b) => a.timestamp - b.timestamp),
      );
    }
  }
  return data;
}

function lowerBoundByTimestamp(data: OHLCData[], target: number): number {
  let lo = 0;
  let hi = data.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (data[mid].timestamp < target) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

function ohlcSeriesMetaEqual(a: OHLCData[], b: OHLCData[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;
  return (
    a[0].timestamp === b[0].timestamp &&
    a[a.length - 1].timestamp === b[b.length - 1].timestamp
  );
}

function symbolsMatch(a: string, b: string): boolean {
  return a.trim().toUpperCase() === b.trim().toUpperCase();
}

function isSlotSymbolCurrent(
  slot: ChartSlotState,
  expectedCurrency: string,
  expectedEpoch?: number,
): boolean {
  if (!slot.selectedSymbol) return false;
  if (!symbolsMatch(slot.selectedSymbol.currency, expectedCurrency)) return false;
  if (expectedEpoch != null && slot.symbolEpoch !== expectedEpoch) return false;
  return true;
}

interface PendingRequest {
  id: string;
  resolve: (data: OHLCData[]) => void;
  reject: (error: Error) => void;
  minCandles: number;
  maxCandles: number;
  fromTimestamp?: number;
  requestType: "initial" | "more";
  /** Safety timeout (CD-01) — cleared whenever the request settles. */
  timeoutId?: ReturnType<typeof setTimeout>;
}

/** CD-01: never let a pending chart-data promise hang forever. */
const PENDING_REQUEST_TIMEOUT_MS = 15_000;

/** Clears the safety timeout; call right before resolving/rejecting a pending. */
function finalizePending(request: PendingRequest) {
  if (request.timeoutId != null) {
    clearTimeout(request.timeoutId);
    request.timeoutId = undefined;
  }
}

/**
 * Per-panel chart freshness (visibility resync gate, proposal 65). Mirrors the
 * connection-freshness shape (useConnectionFreshnessStore) but scoped to ONE
 * chart panel: the live-candle pump rides rAF, which the browser freezes on a
 * hidden tab, so on wake the gate walks live → stale → resyncing → live and
 * backfills the missing bars before a single tick is allowed to advance the
 * series across the gap.
 */
export type ChartFreshness = "live" | "stale" | "resyncing";

export interface ChartSlotState {
  selectedSymbol: Symbol | null;
  selectedTimeframe: TimeFrame;
  ohlcData: OHLCData[];
  ohlcRevision: number;
  /** Bumped on each symbol change to invalidate in-flight async loads. */
  symbolEpoch: number;
  isRequestingData: boolean;
  pendingRequests: Map<string, PendingRequest>;
  /** Trade overlay ticket shown on this panel (desktop footer highlights this row). */
  focusedTradeTicket: string | null;
  /** Proposal 65: live/stale/resyncing gate state for THIS panel. */
  chartFreshness: ChartFreshness;
  /** Proposal 65: timestamp of the last bar the chart trusted before it went hidden. */
  lastGoodBarTs: number | null;
  /** Proposal 65: how many bars the last resync spliced into the tail (UI counter). */
  backfilledBars: number;
}

function emptySlot(): ChartSlotState {
  return {
    selectedSymbol: null,
    selectedTimeframe: "1m",
    ohlcData: [],
    ohlcRevision: 0,
    symbolEpoch: 0,
    isRequestingData: false,
    pendingRequests: new Map(),
    focusedTradeTicket: null,
    chartFreshness: "live",
    lastGoodBarTs: null,
    backfilledBars: 0,
  };
}

function initialSlots(): Record<ChartPanelId, ChartSlotState> {
  return {
    0: emptySlot(),
    1: emptySlot(),
    2: emptySlot(),
    3: emptySlot(),
  };
}

/** Desktop footer / navigation: select a trade overlay once the chart panel shows this symbol. */
export interface PendingTradeChartFocus {
  panelId: ChartPanelId;
  ticket: string;
  symbolCurrency: string;
  /** When SL is unset server-side, turn on interactive SL line with default price (TradeOverlayNew). */
  activateSlPlacement?: boolean;
  /** When TP is unset server-side, turn on interactive TP line with default price (TradeOverlayNew). */
  activateTpPlacement?: boolean;
}

export interface ChartStore3 {
  slots: Record<ChartPanelId, ChartSlotState>;

  /** Pending trade highlight for a specific panel (consumed in Chart.tsx when symbol matches). */
  pendingTradeChartFocus: PendingTradeChartFocus | null;
  /** Bumped on each request so Chart can react even when symbol is unchanged. */
  tradeFocusGeneration: number;

  setSelectedSymbol: (symbol: Symbol | null, panelId?: ChartPanelId) => void;
  setSelectedTimeframe: (
    timeframe: TimeFrame,
    panelId?: ChartPanelId,
  ) => void;
  processBatch: (batch: OHLCBatchResponse) => void;

  requestTradeChartFocus: (req: PendingTradeChartFocus) => void;
  clearPendingTradeChartFocus: () => void;

  setFocusedTradeTicket: (
    panelId: ChartPanelId,
    ticket: string | null,
  ) => void;

  getInitialData: (panelId?: ChartPanelId) => Promise<OHLCData[]>;
  getMoreData: (
    fromTimestamp: number,
    panelId?: ChartPanelId,
  ) => Promise<OHLCData[]>;

  // ── Visibility resync gate (proposal 65) ───────────────────────────────
  /** Freeze the panel: mark stale and remember the last trusted bar timestamp. */
  markChartStale: (panelId: ChartPanelId, lastGoodBarTs?: number | null) => void;
  /** Backfill in flight — hold live ticks until the missing range is spliced. */
  markChartResyncing: (panelId: ChartPanelId) => void;
  /** Gap filled — resume live ticks on this panel. */
  markChartLive: (panelId: ChartPanelId) => void;
  /**
   * Backfill the missing [fromTs, toTs] range and merge it into the tail,
   * deduped + sorted by timestamp (reuses GetOHLCData via
   * sendDataRequestWithRange — no new protocol). Long-sleep cap: when the gap
   * exceeds BACKFILL_MAX_BARS it falls back to a clean rolling re-init instead
   * of requesting hours of 1m bars. Returns the re-aggregated visible tail to
   * apply to the chart plus how many bars were spliced.
   */
  backfillRange: (
    panelId: ChartPanelId,
    fromTs: number,
    toTs: number,
  ) => Promise<ChartBackfillResult>;
}

export interface ChartBackfillResult {
  /** Re-aggregated visible tail for the current timeframe, ready for applyNewData. */
  bars: OHLCData[];
  /** How many bars were spliced into the tail by this backfill. */
  backfilledBars: number;
  /** True when the gap exceeded the cap and a clean rolling re-init was used. */
  reinit: boolean;
}

const processBatchQueue: OHLCBatchResponse[] = [];
let processBatchRafId: number | null = null;
let flushProcessBatchQueueImpl: ((batches: OHLCBatchResponse[]) => void) | null =
  null;

// ── Visibility resync gate (proposal 65) ──────────────────────────────────
/**
 * Above this many missing bars a tail backfill of [lastGoodBarTs, now] would be
 * an enormous single range (e.g. hours of 1m bars after an overnight sleep), so
 * backfillRange falls back to a clean rolling re-init instead. Chosen at 500 —
 * comfortably past the 200-bar initial-load window (getInitialData) so ordinary
 * short/medium hides still splice, but a long sleep can't request hours of bars.
 */
const BACKFILL_MAX_BARS = 500;
/** How long backfillRange waits for the requested batch stream to land before giving up. */
const BACKFILL_BATCH_TIMEOUT_MS = 8000;

/**
 * Symbol (uppercase) → resolvers waiting for the next COMPLETED GetOHLCData
 * batch stream. The batch flush resolves these when it sees `isComplete` for a
 * symbol, letting backfillRange await the merged tail instead of racing the rAF
 * batch queue. Best-effort: each waiter also self-resolves on a timeout.
 */
const backfillWaiters = new Map<string, Array<() => void>>();

function resolveBackfillWaiters(symbolUpper: string): void {
  const arr = backfillWaiters.get(symbolUpper);
  if (!arr) return;
  backfillWaiters.delete(symbolUpper);
  for (const resolve of arr) resolve();
}

function waitForSymbolBatch(
  symbolUpper: string,
  timeoutMs: number,
): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const arr = backfillWaiters.get(symbolUpper) ?? [];
    arr.push(finish);
    backfillWaiters.set(symbolUpper, arr);
    setTimeout(finish, timeoutMs);
  });
}

function runQueuedProcessBatches() {
  processBatchRafId = null;
  if (processBatchQueue.length === 0) return;
  const batches = processBatchQueue.splice(0, processBatchQueue.length);
  flushProcessBatchQueueImpl?.(batches);
}

function scheduleProcessBatchFlush() {
  if (processBatchRafId != null) return;
  if (typeof requestAnimationFrame === "function") {
    processBatchRafId = requestAnimationFrame(() => runQueuedProcessBatches());
  } else {
    processBatchRafId = setTimeout(() => {
      processBatchRafId = null;
      runQueuedProcessBatches();
    }, 0) as unknown as number;
  }
}

const generateRequestId = () =>
  `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getTimeframeMultiplier = (timeframe: TimeFrame): number => {
  switch (timeframe) {
    case "1m":
      return 1;
    case "5m":
      return 5;
    case "15m":
      return 15;
    case "30m":
      return 30;
    case "1h":
      return 60;
    case "2h":
      return 120;
    case "4h":
      return 240;
    case "12h":
      return 720;
    case "1d":
      return 1440;
    case "1w":
      return 10080;
    case "1mn":
      return 43200;
    default:
      return 1;
  }
};

const getRequestDays = (timeframe: TimeFrame): number => {
  switch (timeframe) {
    case "1m":
    case "5m":
    case "15m":
    case "30m":
    case "1h":
    case "2h":
    case "4h":
    case "12h":
      return 50;
    // CD-04: coarse TFs are aggregated client-side from 1m bars, so the
    // request window must cover maxCandles (200) buckets in calendar days
    // (weekends included). Capped at ~10 years to bound server load.
    case "1d":
      return 320; // 200 trading days ≈ 290 calendar days + slack
    case "1w":
      return 1500; // 200 weeks = 1400 days + slack
    case "1mn":
      return 3650; // capped: ~120 monthly candles max
    default:
      return 50;
  }
};

const OHLC_SYNC_FORWARD_DAYS = 7;

function applySyncForwardBuffer(to: Date, forwardDays: number): Date {
  const buffered = new Date(to);
  buffered.setDate(buffered.getDate() + forwardDays);
  return buffered;
}

const calculateRequestRange = (
  fromTimestamp: number,
  days: number,
): { from: Date; to: Date } => {
  const to = new Date(fromTimestamp);
  to.setHours(23, 59, 59, 999);

  const from = new Date(to);
  from.setDate(from.getDate() - days);
  from.setHours(0, 0, 0, 0);

  return { from, to };
};

function getDataForTimeframe(
  data: OHLCData[],
  timeframe: TimeFrame,
  maxCandles: number,
  fromStart: boolean,
): OHLCData[] {
  if (data.length === 0) return [];

  if (timeframe === "1m") {
    return fromStart ? data.slice(0, maxCandles) : data.slice(-maxCandles);
  }

  const multiplier = getTimeframeMultiplier(timeframe);
  const neededRawCandles = maxCandles * multiplier;

  const selectedData = fromStart
    ? data.slice(0, neededRawCandles + multiplier)
    : data.slice(-neededRawCandles - multiplier);

  if (selectedData.length === 0) return [];

  let startIndex = 0;
  for (let i = 0; i < selectedData.length; i++) {
    if (isAlignedToTimeframe(selectedData[i].timestamp, timeframe)) {
      startIndex = i;
      break;
    }
  }

  const alignedData = selectedData.slice(startIndex);
  return fromStart
    ? alignedData.slice(0, neededRawCandles)
    : alignedData.slice(-neededRawCandles);
}

/**
 * Drops leading bars until the first bucket-aligned timestamp so aggregation
 * never starts mid-bucket. Input MUST be ascending.
 */
function ascendingAlignedSlice(
  data: OHLCData[],
  timeframe: TimeFrame,
): OHLCData[] {
  for (let i = 0; i < data.length; i++) {
    if (isAlignedToTimeframe(data[i].timestamp, timeframe)) {
      return i > 0 ? data.slice(i) : data;
    }
  }
  return data;
}

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

/**
 * UTC/epoch alignment (CD-10/CD-15): server bar times are UTC and bucketing
 * (kline-helpers-new.ts, aggregation.worker.ts) floors in UTC — this must
 * match those bucket boundaries, never local time.
 */
function isAlignedToTimeframe(timestamp: number, timeframe: TimeFrame): boolean {
  switch (timeframe) {
    case "5m":
    case "15m":
    case "30m":
    case "1h":
    case "2h":
    case "4h":
    case "12h":
      return timestamp % (getTimeframeMultiplier(timeframe) * MINUTE_MS) === 0;
    case "1d":
      return timestamp % DAY_MS === 0;
    case "1w": {
      if (timestamp % DAY_MS !== 0) return false;
      return new Date(timestamp).getUTCDay() === 1; // Monday 00:00 UTC
    }
    case "1mn": {
      const date = new Date(timestamp);
      return (
        date.getUTCDate() === 1 &&
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0
      );
    }
    default:
      return true;
  }
}

async function sendDataRequest(symbol: Symbol, days: number) {
  try {
    const { VybeTraderClient } = await import(
      "../../connection/client/VybeTraderClient"
    );
    const client = VybeTraderClient.getInstance();

    if (!client.isConnected()) {
      throw new Error("Not connected to trading server");
    }

    const now = new Date();
    const { from, to: rangeTo } = calculateRequestRange(now.getTime(), days);
    const to = applySyncForwardBuffer(rangeTo, OHLC_SYNC_FORWARD_DAYS);
    const key = ohlcRequestKey(symbol.currency, from, to);
    if (inflightOhLcRequestKeys.has(key)) {
      return;
    }
    inflightOhLcRequestKeys.add(key);

    const request: OHLCRequest = {
      symbol: symbol.currency,
      period: 0,
      customFrom: from.toISOString(),
      customTo: to.toISOString(),
    };

    try {
      await client.send("GetOHLCData", request);
    } finally {
      inflightOhLcRequestKeys.delete(key);
    }
  } catch (error) {
    throw error;
  }
}

async function sendDataRequestWithRange(symbol: Symbol, from: Date, to: Date) {
  try {
    const { VybeTraderClient } = await import(
      "../../connection/client/VybeTraderClient"
    );
    const client = VybeTraderClient.getInstance();

    if (!client.isConnected()) {
      throw new Error("Not connected to trading server");
    }

    const key = ohlcRequestKey(symbol.currency, from, to);
    if (inflightOhLcRequestKeys.has(key)) {
      return;
    }
    inflightOhLcRequestKeys.add(key);

    const request: OHLCRequest = {
      symbol: symbol.currency,
      period: 0,
      customFrom: from.toISOString(),
      customTo: to.toISOString(),
    };

    try {
      await client.send("GetOHLCData", request);
    } finally {
      inflightOhLcRequestKeys.delete(key);
    }
  } catch (error) {
    throw error;
  }
}

/**
 * CD-01: best-effort result for a pending request when we can no longer wait
 * for minCandles (final batch arrived or safety timeout fired). Mirrors the
 * strict paths in processPendingForSlot but without the minCandles gate.
 */
async function computePartialResult(
  series: OHLCData[],
  tf: TimeFrame,
  request: PendingRequest,
): Promise<OHLCData[]> {
  const { maxCandles, fromTimestamp, requestType } = request;
  if (requestType === "initial") {
    if (tf === "1m") return series.slice(-maxCandles);
    const dataForAggregation = getDataForTimeframe(series, tf, maxCandles, false);
    if (dataForAggregation.length === 0) return [];
    const aggregated = await aggregationWorkerManager.aggregate(
      dataForAggregation,
      tf,
    );
    return aggregated.slice(-maxCandles);
  }
  if (!fromTimestamp) return [];
  // RP-06: series is ascending — binary-search the boundary instead of
  // filtering the whole series. `upper` = count of bars before fromTimestamp.
  const upper = lowerBoundByTimestamp(series, fromTimestamp);
  if (upper === 0) return [];
  if (tf === "1m") return series.slice(Math.max(0, upper - maxCandles), upper);
  const multiplier = getTimeframeMultiplier(tf);
  const need = maxCandles * multiplier + multiplier;
  const start = Math.max(0, upper - need);
  const truncated = start > 0;
  const ascendingSlice = series.slice(start, upper);
  const dataForAggregation = ascendingAlignedSlice(ascendingSlice, tf);
  if (dataForAggregation.length === 0) return [];
  const aggregated = await aggregationWorkerManager.aggregate(
    dataForAggregation,
    tf,
  );
  const trimmed =
    truncated && aggregated.length > 1 ? aggregated.slice(1) : aggregated;
  return trimmed.slice(-maxCandles);
}

/** CD-15: gap-integrity guard between cached and freshly downloaded ranges. */
const OHLC_GAP_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000; // > any weekend
const OHLC_GAP_MAX_SPAN_MS = 90 * 24 * 60 * 60 * 1000;
/** Each detected gap is requested at most once per session (avoids loops when the server truly has no data there). */
const attemptedGapRequestKeys = new Set<string>();

function requestMissingGaps(symbol: Symbol, series: OHLCData[]) {
  if (series.length < 2) return;
  // Newest-first; fire at most one bounded backfill request per pass.
  for (let i = series.length - 1; i > 0; i--) {
    const gapMs = series[i].timestamp - series[i - 1].timestamp;
    if (gapMs <= OHLC_GAP_THRESHOLD_MS) continue;
    const from = new Date(series[i - 1].timestamp);
    const to = new Date(
      Math.min(series[i].timestamp, series[i - 1].timestamp + OHLC_GAP_MAX_SPAN_MS),
    );
    const key = ohlcRequestKey(symbol.currency, from, to);
    if (attemptedGapRequestKeys.has(key)) continue;
    attemptedGapRequestKeys.add(key);
    void sendDataRequestWithRange(symbol, from, to).catch(() => {
      /* offline / hub down — leave the gap; key stays consumed for this session */
    });
    return;
  }
}

export const useChartStore3 = create<ChartStore3>((set, get) => {
  flushProcessBatchQueueImpl = (batches: OHLCBatchResponse[]) => {
    if (batches.length === 0) return;

    // CD-01: symbols whose requested range's batch stream has completed —
    // pendings for these may resolve with partial data below minCandles.
    const finalBatchSymbols = new Set<string>();
    for (const batch of batches) {
      if (batch.success && batch.isComplete) {
        finalBatchSymbols.add(batch.symbol.trim().toUpperCase());
      }
    }

    set((root) => {
      let slots = { ...root.slots };
      let anyOhlcChange = false;

      const symbolsPersistedOffChart = new Set<string>();

      for (const slotId of CHART_PANEL_IDS) {
        const slot = slots[slotId];
        const sym = slot.selectedSymbol;
        if (!sym) continue;

        const allNew: OHLCData[] = [];
        for (const batch of batches) {
          if (!batch.success || !symbolsMatch(batch.symbol, sym.currency)) continue;
          allNew.push(...ohlcBarsFromBatch(batch));
        }

        if (allNew.length === 0) continue;
        symbolsPersistedOffChart.add(sym.currency);

        const mergeBase = slot.ohlcData;
        const mergedSeries =
          allNew.length > 0 ? mergeDedupeSorted(mergeBase, allNew) : mergeBase;

        const ohlcChanged = !ohlcSeriesMetaEqual(mergeBase, mergedSeries);
        if (ohlcChanged) {
          slots[slotId] = {
            ...slot,
            ohlcData: mergedSeries,
            ohlcRevision: slot.ohlcRevision + 1,
          };
          anyOhlcChange = true;
        }

        const persistKey = generateAccountSymbolKeyForCurrent(sym);
        if (persistKey && allNew.length > 0) {
          ohlcIdbDayCache.enqueuePutBars(persistKey, allNew);
        }
      }

      for (const batch of batches) {
        if (!batch.success) continue;
        const cur = batch.symbol;
        if (symbolsPersistedOffChart.has(cur)) continue;
        if (!isOhlcPrefetchTarget(cur)) continue;
        const allNew = ohlcBarsFromBatch(batch);
        if (allNew.length === 0) continue;
        const account = getCurrentAccount();
        if (!account?.companyName || !account?.serverName) continue;
        const persistKey = generateAccountSymbolKey(
          account.companyName,
          account.serverName,
          cur,
        );
        ohlcIdbDayCache.enqueuePutBars(persistKey, allNew);
      }

      return anyOhlcChange ? { slots } : root;
    });

    for (const slotId of CHART_PANEL_IDS) {
      const slotAfter = get().slots[slotId];
      if (slotAfter.selectedSymbol) {
        // CD-15: re-request large holes between cached and fresh ranges.
        requestMissingGaps(slotAfter.selectedSymbol, slotAfter.ohlcData);
      }
      void processPendingForSlot(slotId, set, get, finalBatchSymbols);
    }

    // Proposal 65: the merge above already spliced these symbols' completed
    // batch streams into their slots (deduped + sorted) — wake any resync
    // backfill that is awaiting the missing range for one of them.
    for (const symbolUpper of finalBatchSymbols) {
      resolveBackfillWaiters(symbolUpper);
    }
  };

  async function processPendingForSlot(
    slotId: ChartPanelId,
    setArg: typeof set,
    getArg: typeof get,
    finalBatchSymbols: ReadonlySet<string> = new Set(),
  ) {
    const slotNow = getArg().slots[slotId];
    const sym = slotNow.selectedSymbol;
    if (!sym) return;

    const mergedSeries = slotNow.ohlcData;
    const pendingSnapshot = new Map(slotNow.pendingRequests);
    const updatedRequests = new Map(pendingSnapshot);
    // Ids this run actually settled (resolved/rejected). The final write-back
    // reconciles the live map against these instead of replacing it wholesale,
    // so entries added or removed concurrently during awaits are preserved.
    const settledIds = new Set<string>();
    // A request may be settled concurrently (e.g. forceResolvePending's 15s
    // timeout) while we await aggregation — never settle an id that is no
    // longer in the live map.
    const settleResolve = (
      id: string,
      request: PendingRequest,
      data: OHLCData[],
    ) => {
      updatedRequests.delete(id);
      if (!getArg().slots[slotId].pendingRequests.has(id)) return;
      finalizePending(request);
      request.resolve(ensureAscendingDeduped(data));
      settledIds.add(id);
    };
    const settleReject = (id: string, request: PendingRequest, error: Error) => {
      updatedRequests.delete(id);
      if (!getArg().slots[slotId].pendingRequests.has(id)) return;
      finalizePending(request);
      request.reject(error);
      settledIds.add(id);
    };

    for (const [id, request] of pendingSnapshot.entries()) {
      const slotNow = getArg().slots[slotId];
      if (
        !slotNow.selectedSymbol ||
        !symbolsMatch(slotNow.selectedSymbol.currency, sym.currency)
      ) {
        // Symbol changed mid-loop — stop processing, but still fall through to
        // the reconcile so already-settled ids are removed from the live map.
        break;
      }
      const { minCandles, maxCandles, fromTimestamp, requestType } = request;
      const tf = getArg().slots[slotId].selectedTimeframe;

      try {
        if (requestType === "initial") {
          if (tf === "1m") {
            if (mergedSeries.length >= minCandles) {
              settleResolve(id, request, mergedSeries.slice(-maxCandles));
            }
          } else {
            const dataForAggregation = getDataForTimeframe(
              mergedSeries,
              tf,
              maxCandles,
              false,
            );

            if (dataForAggregation.length >= minCandles) {
              const aggregatedData = await aggregationWorkerManager.aggregate(
                dataForAggregation,
                tf,
              );

              if (aggregatedData.length >= minCandles) {
                settleResolve(id, request, aggregatedData.slice(-maxCandles));
              }
            }
          }
        } else if (requestType === "more" && fromTimestamp) {
          // RP-06: mergedSeries is ascending — binary-search the boundary
          // instead of filtering the whole series (getMoreData does the same).
          // `upper` is the count of bars strictly before fromTimestamp.
          const upper = lowerBoundByTimestamp(mergedSeries, fromTimestamp);

          if (tf === "1m") {
            if (upper >= minCandles) {
              // Newest maxCandles bars strictly before fromTimestamp,
              // preserving the series' ascending order.
              const start = Math.max(0, upper - maxCandles);
              settleResolve(id, request, mergedSeries.slice(start, upper));
            }
          } else {
            if (upper >= minCandles) {
              const multiplier = getTimeframeMultiplier(tf);
              const need = maxCandles * multiplier + multiplier;
              const start = Math.max(0, upper - need);
              const truncated = start > 0;
              const ascendingSlice = mergedSeries.slice(start, upper);
              const dataForAggregation = ascendingAlignedSlice(
                ascendingSlice,
                tf,
              );

              if (dataForAggregation.length >= minCandles) {
                const aggregatedData = await aggregationWorkerManager.aggregate(
                  dataForAggregation,
                  tf,
                );

                if (aggregatedData.length >= minCandles) {
                  // Oldest bucket may be partial when we truncated older
                  // history — drop it so its OHLC never renders wrong.
                  const trimmed =
                    truncated && aggregatedData.length > 1
                      ? aggregatedData.slice(1)
                      : aggregatedData;
                  settleResolve(id, request, trimmed.slice(-maxCandles));
                }
              }
            }
          }
        }

        // CD-01: the batch stream for this symbol completed but the strict
        // minCandles path didn't fire — resolve with whatever partial data
        // exists rather than hanging forever. Zero-bar case is left to the
        // safety timeout so late out-of-range batches can still complete it.
        if (
          updatedRequests.has(id) &&
          finalBatchSymbols.has(sym.currency.trim().toUpperCase())
        ) {
          const partial = await computePartialResult(mergedSeries, tf, request);
          if (partial.length > 0) {
            settleResolve(id, request, partial);
          }
        }
      } catch (error) {
        settleReject(id, request, error as Error);
      }
    }

    const liveSlot = getArg().slots[slotId];
    const flagStale =
      liveSlot.isRequestingData !== liveSlot.pendingRequests.size > 0;
    if (settledIds.size > 0 || flagStale) {
      setArg((root) => {
        const cur = root.slots[slotId];
        // Reconcile instead of replace: start from the CURRENT live map and
        // delete only the ids this run settled, keeping any entries added or
        // removed concurrently while we awaited.
        const pendingRequests = new Map(cur.pendingRequests);
        for (const settledId of settledIds) {
          pendingRequests.delete(settledId);
        }
        return {
          slots: {
            ...root.slots,
            [slotId]: {
              ...cur,
              pendingRequests,
              isRequestingData: pendingRequests.size > 0,
            },
          },
        };
      });
    }
  }

  /**
   * CD-01: safety-timeout path. Resolves a still-pending request with whatever
   * data exists, or rejects when there is none at all.
   */
  async function forceResolvePending(panelId: ChartPanelId, requestId: string) {
    const slot = get().slots[panelId];
    const request = slot.pendingRequests.get(requestId);
    if (!request) return;

    set((root) => {
      const cur = root.slots[panelId];
      const pendingRequests = new Map(cur.pendingRequests);
      pendingRequests.delete(requestId);
      return {
        slots: {
          ...root.slots,
          [panelId]: {
            ...cur,
            pendingRequests,
            isRequestingData: pendingRequests.size > 0,
          },
        },
      };
    });

    finalizePending(request);
    try {
      const slotNow = get().slots[panelId];
      const partial = await computePartialResult(
        slotNow.ohlcData,
        slotNow.selectedTimeframe,
        request,
      );
      if (partial.length > 0) {
        request.resolve(ensureAscendingDeduped(partial));
      } else {
        request.reject(new Error("OHLC request timed out with no data"));
      }
    } catch (error) {
      request.reject(error as Error);
    }
  }

  return {
    slots: initialSlots(),
    pendingTradeChartFocus: null,
    tradeFocusGeneration: 0,

    requestTradeChartFocus: (req: PendingTradeChartFocus) => {
      set((root) => ({
        pendingTradeChartFocus: req,
        tradeFocusGeneration: root.tradeFocusGeneration + 1,
      }));
    },

    clearPendingTradeChartFocus: () => {
      set({ pendingTradeChartFocus: null });
    },

    setFocusedTradeTicket: (panelId, ticket) => {
      set((root) => ({
        slots: {
          ...root.slots,
          [panelId]: {
            ...root.slots[panelId],
            focusedTradeTicket: ticket,
          },
        },
      }));
    },

    setSelectedSymbol: (symbol: Symbol | null, panelId: ChartPanelId = 0) => {
      const state = get();
      const slot = state.slots[panelId];

      if (slot.selectedSymbol?.currency === symbol?.currency) {
        return;
      }

      slot.pendingRequests.forEach((request) => {
        finalizePending(request);
        request.reject(new Error("Symbol changed"));
      });

      // CD-03: do NOT clear the shared processBatchQueue here — it also carries
      // batches for other panels and off-chart prefetch targets; the flush
      // already validates each batch against every slot's current symbol.

      const nextEpoch = slot.symbolEpoch + 1;

      set((root) => ({
        slots: {
          ...root.slots,
          [panelId]: {
            ...root.slots[panelId],
            selectedSymbol: symbol,
            ohlcData: [],
            ohlcRevision: root.slots[panelId].ohlcRevision + 1,
            symbolEpoch: nextEpoch,
            isRequestingData: false,
            pendingRequests: new Map(),
            focusedTradeTicket: null,
            // Proposal 65: a fresh symbol reloads from scratch — reset the gate.
            chartFreshness: "live",
            lastGoodBarTs: null,
            backfilledBars: 0,
          },
        },
      }));

      // Proposal 74: remember this panel's symbol so the desk comes back on
      // the next app start. Debounced; no-op while a restore is replaying.
      scheduleDeskWrite(get().slots);

      if (!symbol) {
        return;
      }

      const loadSymbol = symbol.currency;
      const loadEpoch = nextEpoch;

      void (async () => {
        const cacheKey = generateAccountSymbolKeyForCurrent(symbol);
        if (cacheKey) {
          try {
            let sorted: OHLCData[] = [];
            const fromDays = await ohlcIdbDayCache.loadSymbolBars(cacheKey);
            if (fromDays.length > 0) {
              sorted = [...fromDays].sort((a, b) => a.timestamp - b.timestamp);
            } else {
              const legacy = await enhancedOHLCIndexDB.loadOHLCData(cacheKey);
              if (legacy && legacy.length > 0) {
                sorted = [...legacy].sort((a, b) => a.timestamp - b.timestamp);
                ohlcIdbDayCache.enqueuePutBars(cacheKey, legacy);
              }
            }
            const slotNow = get().slots[panelId];
            if (
              sorted.length > 0 &&
              isSlotSymbolCurrent(slotNow, loadSymbol, loadEpoch)
            ) {
              const prev = slotNow.ohlcData;
              if (!ohlcSeriesMetaEqual(prev, sorted)) {
                set((root) => ({
                  slots: {
                    ...root.slots,
                    [panelId]: {
                      ...root.slots[panelId],
                      ohlcData: sorted,
                      ohlcRevision: root.slots[panelId].ohlcRevision + 1,
                    },
                  },
                }));
              }
            }
          } catch {
            /* ignore cache errors */
          }
        }

        if (!isSlotSymbolCurrent(get().slots[panelId], loadSymbol, loadEpoch)) {
          return;
        }

        const requestDays = getRequestDays(get().slots[panelId].selectedTimeframe);
        try {
          await sendDataRequest(symbol, requestDays);
        } catch {
          /* Offline / hub down */
        }
      })();
    },

    setSelectedTimeframe: (timeframe: TimeFrame, panelId: ChartPanelId = 0) => {
      const state = get();
      const slot = state.slots[panelId];

      if (slot.selectedTimeframe === timeframe) {
        return;
      }

      slot.pendingRequests.forEach((request) => {
        finalizePending(request);
        request.reject(new Error("Timeframe changed"));
      });

      set((root) => ({
        slots: {
          ...root.slots,
          [panelId]: {
            ...root.slots[panelId],
            selectedTimeframe: timeframe,
            pendingRequests: new Map(),
            isRequestingData: false,
          },
        },
      }));

      // Proposal 74: persist the desk (see setSelectedSymbol).
      scheduleDeskWrite(get().slots);
    },

    processBatch: (batch: OHLCBatchResponse) => {
      const root = get();
      const anyMatch = CHART_PANEL_IDS.some((id) => {
        const sym = root.slots[id].selectedSymbol;
        return !!(sym && symbolsMatch(batch.symbol, sym.currency) && batch.success);
      });
      const prefetchMatch =
        batch.success && isOhlcPrefetchTarget(batch.symbol);
      if (!anyMatch && !prefetchMatch) {
        return;
      }
      processBatchQueue.push(batch);
      scheduleProcessBatchFlush();
    },

    getInitialData: async (panelId: ChartPanelId = 0) => {
      const state = get();
      const slot = state.slots[panelId];
      const minCandles = 20;
      const maxCandles = 200;

      if (!slot.selectedSymbol) {
        throw new Error("No symbol selected");
      }

      const expectedCurrency = slot.selectedSymbol.currency;
      const expectedEpoch = slot.symbolEpoch;

      const assertStillCurrent = () => {
        if (
          !isSlotSymbolCurrent(
            get().slots[panelId],
            expectedCurrency,
            expectedEpoch
          )
        ) {
          throw new Error("Symbol changed");
        }
      };

      if (slot.selectedTimeframe === "1m") {
        if (slot.ohlcData.length >= minCandles) {
          assertStillCurrent();
          return ensureAscendingDeduped(
            get().slots[panelId].ohlcData.slice(-maxCandles),
          );
        }
      } else {
        const dataForAggregation = getDataForTimeframe(
          slot.ohlcData,
          slot.selectedTimeframe,
          maxCandles,
          false,
        );

        if (dataForAggregation.length >= minCandles) {
          const aggregatedData = await aggregationWorkerManager.aggregate(
            dataForAggregation,
            slot.selectedTimeframe,
          );
          assertStillCurrent();
          return ensureAscendingDeduped(aggregatedData.slice(-maxCandles));
        }
      }

      const requestId = generateRequestId();

      return new Promise<OHLCData[]>((resolve, reject) => {
        const pendingEntry: PendingRequest = {
          id: requestId,
          resolve: (data) => {
            if (
              !isSlotSymbolCurrent(
                get().slots[panelId],
                expectedCurrency,
                expectedEpoch
              )
            ) {
              reject(new Error("Symbol changed"));
              return;
            }
            resolve(data);
          },
          reject,
          minCandles,
          maxCandles,
          requestType: "initial",
        };
        pendingEntry.timeoutId = setTimeout(() => {
          void forceResolvePending(panelId, requestId);
        }, PENDING_REQUEST_TIMEOUT_MS);

        // CD-05: build the map from current root state inside the updater —
        // never from the closure snapshot taken at function entry.
        set((root) => {
          const cur = root.slots[panelId];
          const pendingRequests = new Map(cur.pendingRequests);
          pendingRequests.set(requestId, pendingEntry);
          return {
            slots: {
              ...root.slots,
              [panelId]: {
                ...cur,
                pendingRequests,
                isRequestingData: true,
              },
            },
          };
        });

        const requestDays = getRequestDays(slot.selectedTimeframe);
        sendDataRequest(slot.selectedSymbol!, requestDays);
      });
    },

    getMoreData: async (fromTimestamp: number, panelId: ChartPanelId = 0) => {
      const state = get();
      const slot = state.slots[panelId];
      const minCandles = 20;
      const maxCandles = 200;

      if (!slot.selectedSymbol) {
        throw new Error("No symbol selected");
      }

      const expectedCurrency = slot.selectedSymbol.currency;
      const expectedEpoch = slot.symbolEpoch;

      const assertStillCurrent = () => {
        if (
          !isSlotSymbolCurrent(
            get().slots[panelId],
            expectedCurrency,
            expectedEpoch
          )
        ) {
          throw new Error("Symbol changed");
        }
      };

      const upper = lowerBoundByTimestamp(slot.ohlcData, fromTimestamp);
      const olderCount = upper;

      if (slot.selectedTimeframe === "1m") {
        if (olderCount >= minCandles) {
          const start = Math.max(0, upper - maxCandles);
          assertStillCurrent();
          return ensureAscendingDeduped(
            get().slots[panelId].ohlcData.slice(start, upper),
          );
        }
      } else {
        if (olderCount >= minCandles) {
          const multiplier = getTimeframeMultiplier(slot.selectedTimeframe);
          const need = maxCandles * multiplier + multiplier;
          const start = Math.max(0, upper - need);

          const ascendingSlice = slot.ohlcData.slice(start, upper);
          const dataForAggregation = ascendingAlignedSlice(
            ascendingSlice,
            slot.selectedTimeframe,
          );

          const aggregatedData = await aggregationWorkerManager.aggregate(
            dataForAggregation,
            slot.selectedTimeframe,
          );

          if (aggregatedData.length > 0) {
            assertStillCurrent();
            // Oldest bucket may be partial when older history was truncated
            // at `start` — drop it so its OHLC never renders wrong.
            const trimmed =
              start > 0 && aggregatedData.length > 1
                ? aggregatedData.slice(1)
                : aggregatedData;
            return ensureAscendingDeduped(trimmed.slice(-maxCandles));
          }
        }
      }

      const requestId = generateRequestId();

      return new Promise<OHLCData[]>((resolve, reject) => {
        const pendingEntry: PendingRequest = {
          id: requestId,
          resolve: (data) => {
            if (
              !isSlotSymbolCurrent(
                get().slots[panelId],
                expectedCurrency,
                expectedEpoch
              )
            ) {
              reject(new Error("Symbol changed"));
              return;
            }
            resolve(data);
          },
          reject,
          minCandles,
          maxCandles,
          fromTimestamp,
          requestType: "more",
        };
        pendingEntry.timeoutId = setTimeout(() => {
          void forceResolvePending(panelId, requestId);
        }, PENDING_REQUEST_TIMEOUT_MS);

        // CD-05: build the map from current root state inside the updater —
        // never from the closure snapshot taken at function entry.
        set((root) => {
          const cur = root.slots[panelId];
          const pendingRequests = new Map(cur.pendingRequests);
          pendingRequests.set(requestId, pendingEntry);
          return {
            slots: {
              ...root.slots,
              [panelId]: {
                ...cur,
                pendingRequests,
                isRequestingData: true,
              },
            },
          };
        });

        const requestDays = getRequestDays(slot.selectedTimeframe);
        const { from, to } = calculateRequestRange(fromTimestamp, requestDays);
        sendDataRequestWithRange(slot.selectedSymbol!, from, to);
      });
    },

    // ── Visibility resync gate (proposal 65) ─────────────────────────────
    markChartStale: (panelId, lastGoodBarTs) => {
      set((root) => {
        const cur = root.slots[panelId];
        if (
          cur.chartFreshness === "stale" &&
          (lastGoodBarTs == null || cur.lastGoodBarTs === lastGoodBarTs)
        ) {
          return root;
        }
        return {
          slots: {
            ...root.slots,
            [panelId]: {
              ...cur,
              chartFreshness: "stale",
              lastGoodBarTs:
                lastGoodBarTs != null ? lastGoodBarTs : cur.lastGoodBarTs,
              // Reset the counter at the start of a fresh hidden→wake cycle.
              backfilledBars: 0,
            },
          },
        };
      });
    },

    markChartResyncing: (panelId) => {
      set((root) => {
        const cur = root.slots[panelId];
        if (cur.chartFreshness === "resyncing") return root;
        return {
          slots: {
            ...root.slots,
            [panelId]: { ...cur, chartFreshness: "resyncing" },
          },
        };
      });
    },

    markChartLive: (panelId) => {
      set((root) => {
        const cur = root.slots[panelId];
        if (cur.chartFreshness === "live") return root;
        return {
          slots: {
            ...root.slots,
            [panelId]: { ...cur, chartFreshness: "live" },
          },
        };
      });
    },

    backfillRange: async (panelId, fromTs, toTs) => {
      const slot = get().slots[panelId];
      const symbol = slot.selectedSymbol;
      if (!symbol) return { bars: [], backfilledBars: 0, reinit: false };

      const tf = slot.selectedTimeframe;
      const barMs = getTimeframeMultiplier(tf) * MINUTE_MS;
      const missing =
        barMs > 0 ? Math.max(0, Math.floor((toTs - fromTs) / barMs)) : 0;
      // Long-sleep cap: an enormous tight range would ask the server for hours
      // of 1m bars — fall back to a clean rolling re-init instead.
      const reinit = missing > BACKFILL_MAX_BARS;

      const symbolUpper = symbol.currency.trim().toUpperCase();
      const beforeSeries = slot.ohlcData;
      const beforeLastTs =
        beforeSeries.length > 0
          ? beforeSeries[beforeSeries.length - 1].timestamp
          : fromTs;

      // Register the waiter BEFORE the request so a fast batch can't slip past.
      const batchLanded = waitForSymbolBatch(
        symbolUpper,
        BACKFILL_BATCH_TIMEOUT_MS,
      );

      try {
        if (reinit) {
          await sendDataRequest(symbol, getRequestDays(tf));
        } else {
          await sendDataRequestWithRange(
            symbol,
            new Date(fromTs),
            new Date(toTs),
          );
        }
      } catch {
        /* offline / hub down — resolve with whatever the tail already holds */
      }

      // The batch flush merges (deduped + sorted) into slot.ohlcData, then wakes
      // this waiter. Timeout-guarded so an offline wake never hangs the gate.
      await batchLanded;

      const slotNow = get().slots[panelId];
      if (
        !slotNow.selectedSymbol ||
        !symbolsMatch(slotNow.selectedSymbol.currency, symbol.currency)
      ) {
        // Symbol changed mid-backfill — the init path owns the new series.
        return { bars: [], backfilledBars: 0, reinit };
      }

      // Count bars strictly newer than the pre-backfill tail — those are the
      // ones the merge spliced into the gap.
      const series = slotNow.ohlcData;
      const spliceStart = lowerBoundByTimestamp(series, beforeLastTs + 1);
      const backfilledBars = Math.max(0, series.length - spliceStart);

      // Re-aggregate the visible tail for the current TF (same path as init) —
      // now including the freshly spliced bars.
      let bars: OHLCData[] = [];
      try {
        bars = await get().getInitialData(panelId);
      } catch {
        bars = [];
      }

      set((root) => {
        const cur = root.slots[panelId];
        return {
          slots: {
            ...root.slots,
            [panelId]: {
              ...cur,
              backfilledBars,
              lastGoodBarTs:
                series.length > 0
                  ? series[series.length - 1].timestamp
                  : cur.lastGoodBarTs,
            },
          },
        };
      });

      return { bars, backfilledBars, reinit };
    },
  };
});
